/**
 * Main Dashboard component.
 * Layout with sidebar (navigator), tabs area (editor), and bottom panel (history).
 * Inspired by DBeaver's main interface.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  Divider,
  Tooltip,
  TextField,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Connection, SqlTab, TableInfo } from '../types';
import { getTabs, saveTabs, getActiveTabId, setActiveTabId, getPreferences, savePreferences, removeToken, getConnections, saveConnections } from '../services/storage';
import { connectionApi, tabsApi } from '../services/api';
import DatabaseNavigator from './DatabaseNavigator';
import SqlEditorTab from './SqlEditorTab';
import QueryHistory from './QueryHistory';
import ConnectionModal from './ConnectionModal';
import NounProjectIcon from './NounProjectIcon';

const DRAWER_WIDTH = 280;
const BOTTOM_PANEL_HEIGHT = 200;

const MainDashboard: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [tabs, setTabs] = useState<SqlTab[]>([]);
  const [activeTabId, setActiveTabIdState] = useState<string>('');
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [autoCommit, setAutoCommit] = useState(true);
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const [navigatorRefreshTrigger, setNavigatorRefreshTrigger] = useState(0);
  const [queryHistoryRefreshTrigger, setQueryHistoryRefreshTrigger] = useState(0);
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingTabTitle, setEditingTabTitle] = useState<string>('');

  useEffect(() => {
    loadInitialState();
    
    // Load preferences
    const prefs = getPreferences();
    setAutoCommit(prefs.autoCommit);
  }, []);

  // Function to save tabs to backend immediately
  const saveTabsToBackend = async (tabsToSave: SqlTab[]) => {
    if (tabsToSave.length === 0) return;
    
    try {
      // Convert SqlTab to format expected by backend (remove results to save space)
      const tabsForBackend = tabsToSave.map(tab => ({
        ...tab,
        results: undefined, // Don't save query results, just metadata
      }));
      console.log('Saving tabs to backend:', tabsForBackend);
      const saved = await tabsApi.saveAll(tabsForBackend);
      console.log('Tabs saved successfully to backend:', saved);
    } catch (err) {
      console.error('Error saving tabs to backend:', err);
      // Continue working with localStorage as fallback
    }
  };

  useEffect(() => {
    // Save tabs to localStorage (for quick access)
    saveTabs(tabs);
    
    // Save tabs to backend (with debouncing to avoid too many requests)
    const timeoutId = setTimeout(() => {
      saveTabsToBackend(tabs);
    }, 500); // Reduced debounce to 500ms for faster saves
    
    return () => clearTimeout(timeoutId);
  }, [tabs]);

  // Save tabs immediately when page is about to be hidden (refresh, close, navigate)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && tabs.length > 0) {
        // Page is about to be hidden - save immediately
        const tabsForBackend = tabs.map(tab => ({
          ...tab,
          results: undefined,
        }));
        
        // Use tabsApi but with a fire-and-forget approach
        // The keepalive flag ensures the request completes even if page closes
        tabsApi.saveAll(tabsForBackend).catch(err => {
          console.error('Error saving tabs on page unload:', err);
        });
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [tabs]);

  const loadInitialState = async () => {
    // Load connections from backend API, fallback to localStorage
    let conns: Connection[] = [];
    try {
      conns = await connectionApi.getAll();
      console.log('Loaded connections from backend:', conns);
      
      // Save to localStorage as backup
      if (conns.length > 0) {
        saveConnections(conns);
      }
      
      // If backend returns empty, try localStorage as fallback
      if (conns.length === 0) {
        const localConns = getConnections();
        console.log('Backend returned empty, trying localStorage:', localConns);
        if (localConns.length > 0) {
          conns = localConns;
        }
      }
    } catch (err) {
      console.error('Error loading connections from backend, using localStorage:', err);
      // Fallback to localStorage if backend is unavailable
      conns = getConnections();
    }
    setConnections(conns);

    // Load tabs from backend API, fallback to localStorage
    let savedTabs: SqlTab[] = [];
    try {
      savedTabs = await tabsApi.getAll();
      console.log('Loaded tabs from backend:', savedTabs);
      
      // If backend returns empty, try localStorage as fallback
      if (savedTabs.length === 0) {
        const localTabs = getTabs();
        console.log('Backend returned empty, trying localStorage:', localTabs);
        if (localTabs.length > 0) {
          savedTabs = localTabs;
          // Also save localStorage tabs to backend
          saveTabsToBackend(localTabs);
        }
      }
    } catch (err) {
      console.error('Error loading tabs from backend, using localStorage:', err);
      // Fallback to localStorage if backend is unavailable
      savedTabs = getTabs();
    }
    
    // If still no tabs, create a default one
    if (savedTabs.length === 0) {
      savedTabs = [createNewTab()];
    }
    setTabs(savedTabs);

    // Load active tab
    const activeId = getActiveTabId() || savedTabs[0]?.id;
    setActiveTabIdState(activeId);
  };

  const createNewTab = (): SqlTab => {
    const id = `tab_${Date.now()}`;
    return {
      id,
      title: `Query ${tabs.length + 1}`,
      sql: '',
      connectionId: selectedConnectionId || undefined,
      isDirty: false,
    };
  };

  const handleNewTab = () => {
    const newTab = createNewTab();
    setTabs([...tabs, newTab]);
    setActiveTabIdState(newTab.id);
    setActiveTabId(newTab.id);
  };

  const handleCloseTab = (tabId: string) => {
    if (tabs.length === 1) {
      // Don't close the last tab
      return;
    }

    const index = tabs.findIndex(t => t.id === tabId);
    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);

    // Set active tab to previous or next
    if (activeTabId === tabId) {
      const newActiveTab = newTabs[Math.max(0, index - 1)];
      setActiveTabIdState(newActiveTab.id);
      setActiveTabId(newActiveTab.id);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTabIdState(newValue);
    setActiveTabId(newValue);
  };

  const handleUpdateTab = (updatedTab: SqlTab) => {
    setTabs(tabs.map(t => t.id === updatedTab.id ? updatedTab : t));
    
    // If the tab has results, it means a query was just executed
    // Refresh the query history to show the new entry
    if (updatedTab.results) {
      setQueryHistoryRefreshTrigger(prev => prev + 1);
    }
  };

  const handleStartRenameTab = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      setEditingTabId(tabId);
      setEditingTabTitle(tab.title);
    }
  };

  const handleFinishRenameTab = async () => {
    if (editingTabId && editingTabTitle.trim()) {
      const updatedTab = {
        ...tabs.find(t => t.id === editingTabId)!,
        title: editingTabTitle.trim(),
      };
      handleUpdateTab(updatedTab);
      
      // Save immediately after rename (critical operation)
      const updatedTabs = tabs.map(t => t.id === editingTabId ? updatedTab : t);
      await saveTabsToBackend(updatedTabs);
    }
    setEditingTabId(null);
    setEditingTabTitle('');
  };

  const handleCancelRenameTab = () => {
    setEditingTabId(null);
    setEditingTabTitle('');
  };

  const handleSelectConnection = (connection: Connection) => {
    setSelectedConnectionId(connection.id);
  };

  const handleNewConnection = () => {
    setEditingConnection(null);
    setConnectionModalOpen(true);
  };

  const handleEditConnection = (connection: Connection) => {
    setEditingConnection(connection);
    setConnectionModalOpen(true);
  };

  const handleSaveConnection = async (connection: Connection) => {
    try {
      // Connection is already saved to backend via ConnectionModal
      // Just reload connections from backend
      const conns = await connectionApi.getAll();
      setConnections(conns);
      
      // Trigger navigator refresh
      setNavigatorRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error reloading connections:', err);
    }
  };

  const handleInsertTable = (table: TableInfo) => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (activeTab) {
      // Include schema name if available, otherwise just use table name
      const tableRef = table.schema ? `${table.schema}.${table.name}` : table.name;
      const sql = `SELECT * FROM ${tableRef}`;
      handleUpdateTab({
        ...activeTab,
        sql: activeTab.sql ? `${activeTab.sql}\n${sql}` : sql,
        isDirty: true,
      });
    }
  };

  const handleSelectQuery = (sql: string) => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (activeTab) {
      handleUpdateTab({
        ...activeTab,
        sql,
        isDirty: true,
      });
    }
  };

  const handleToggleAutoCommit = () => {
    const newValue = !autoCommit;
    setAutoCommit(newValue);
    
    // Save to preferences
    const prefs = getPreferences();
    prefs.autoCommit = newValue;
    savePreferences(prefs);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      removeToken();
      window.location.reload();
    }
  };

  const activeTab = tabs.find(t => t.id === activeTabId);

  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={1}>
        <Toolbar variant="dense">
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <NounProjectIcon iconName="database-8067727" size={28} />
            SQL Client
          </Typography>
          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Sidebar - Database Navigator */}
        <Drawer
          variant="persistent"
          anchor="left"
          open={drawerOpen}
          sx={{
            width: drawerOpen ? DRAWER_WIDTH : 0,
            flexShrink: 0,
            transition: 'width 0.3s ease',
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              position: 'relative',
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 0.5, borderBottom: 1, borderColor: 'divider' }}>
            <IconButton
              size="small"
              onClick={() => setDrawerOpen(false)}
              sx={{ mr: 0.5 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Box>
          <DatabaseNavigator
            onSelectConnection={handleSelectConnection}
            onNewConnection={handleNewConnection}
            onEditConnection={handleEditConnection}
            onInsertTable={handleInsertTable}
            refreshTrigger={navigatorRefreshTrigger}
          />
        </Drawer>
        
        {/* Collapse button when drawer is closed */}
        {!drawerOpen && (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1300,
            }}
          >
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderLeft: 'none',
                borderRadius: '0 4px 4px 0',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Main Content Area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Tabs Header */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
            <Tabs
              value={activeTabId}
              onChange={handleTabChange}
              sx={{ flexGrow: 1 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  label={
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleStartRenameTab(tab.id);
                      }}
                    >
                      {editingTabId === tab.id ? (
                        <TextField
                          value={editingTabTitle}
                          onChange={(e) => setEditingTabTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              handleFinishRenameTab();
                            } else if (e.key === 'Escape') {
                              e.stopPropagation();
                              handleCancelRenameTab();
                            }
                          }}
                          onBlur={handleFinishRenameTab}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          size="small"
                          sx={{
                            minWidth: 100,
                            '& .MuiInputBase-root': {
                              fontSize: '0.875rem',
                              height: '24px',
                            },
                            '& .MuiInputBase-input': {
                              padding: '2px 8px',
                            },
                          }}
                          variant="standard"
                        />
                      ) : (
                        <>
                          {tab.title}
                          {tab.isDirty && ' *'}
                        </>
                      )}
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseTab(tab.id);
                        }}
                        sx={{
                          ml: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '50%',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </Box>
                    </Box>
                  }
                />
              ))}
            </Tabs>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleNewTab}
              sx={{ mr: 1 }}
            >
              New Tab
            </Button>
          </Box>

          {/* Active Tab Content */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            {activeTab && (
              <SqlEditorTab
                tab={activeTab}
                connections={connections}
                selectedConnectionId={selectedConnectionId}
                autoCommit={autoCommit}
                onUpdateTab={handleUpdateTab}
                onSelectConnection={setSelectedConnectionId}
                onToggleAutoCommit={handleToggleAutoCommit}
              />
            )}
          </Box>
        </Box>

        {/* Right Sidebar - Query History */}
        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              position: 'relative',
            },
          }}
        >
          <QueryHistory 
            onSelectQuery={handleSelectQuery} 
            refreshTrigger={queryHistoryRefreshTrigger}
          />
        </Drawer>
      </Box>

      {/* Connection Modal */}
      <ConnectionModal
        open={connectionModalOpen}
        connection={editingConnection}
        onClose={() => setConnectionModalOpen(false)}
        onSave={handleSaveConnection}
      />
    </Box>
  );
};

export default MainDashboard;

